/**
 * Invitation API
 *
 * Mock invitation service for coordinating challenges together.
 * NOTE: Invitations are DISTINCT from Gifts - they coordinate doing challenges TOGETHER
 */

import type {
  ChallengeInvitation,
  InvitationRecipient,
  CreateInvitationRequest,
  InvitationRecipientSearchResult,
  InvitationPermission,
  InvitationPermissionType,
  Challenge,
  ApiResponse,
  UUID,
  User,
} from '../types'
import { mockDb, delay, timestamp } from './mockDb'
import { success, error } from './client'

// Session key (shared)
const SESSION_KEY = 'docurious-session'

function getCurrentUserId(): string | null {
  const stored = localStorage.getItem(SESSION_KEY)
  if (!stored) return null
  try {
    return JSON.parse(stored).userId
  } catch {
    return null
  }
}

/**
 * Invitation API
 */
export const invitationApi = {
  // ============================================================================
  // Sent Invitations
  // ============================================================================

  /**
   * Get invitations sent by the current user
   */
  async getSentInvitations(): Promise<ApiResponse<ChallengeInvitation[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const invitations = mockDb
      .findWhere<ChallengeInvitation>('challengeInvitations', (inv) => inv.senderId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Populate challenge and recipients
    const populatedInvitations = invitations.map((inv) => populateInvitation(inv))

    return success(populatedInvitations)
  },

  // ============================================================================
  // Received Invitations
  // ============================================================================

  /**
   * Get invitations received by the current user
   */
  async getReceivedInvitations(): Promise<ApiResponse<ChallengeInvitation[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const user = mockDb.findById<User>('users', userId)
    if (!user) {
      return error('User not found')
    }

    // Find invitation recipients that match this user
    const recipientRecords = mockDb.findWhere<InvitationRecipient>(
      'invitationRecipients',
      (r) => r.recipientUserId === userId || r.recipientValue === user.email
    )

    const invitationIds = [...new Set(recipientRecords.map((r) => r.invitationId))]

    const invitations = invitationIds
      .map((id) => mockDb.findById<ChallengeInvitation>('challengeInvitations', id))
      .filter((inv): inv is ChallengeInvitation => inv !== null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    // Populate
    const populatedInvitations = invitations.map((inv) => populateInvitation(inv))

    return success(populatedInvitations)
  },

  /**
   * Get pending invitations for the current user
   */
  async getPendingInvitations(): Promise<ApiResponse<ChallengeInvitation[]>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const user = mockDb.findById<User>('users', userId)
    if (!user) {
      return error('User not found')
    }

    // Find pending recipient records for this user
    const recipientRecords = mockDb.findWhere<InvitationRecipient>(
      'invitationRecipients',
      (r) =>
        (r.recipientUserId === userId || r.recipientValue === user.email) &&
        r.status === 'pending'
    )

    const invitationIds = [...new Set(recipientRecords.map((r) => r.invitationId))]

    const invitations = invitationIds
      .map((id) => mockDb.findById<ChallengeInvitation>('challengeInvitations', id))
      .filter(
        (inv): inv is ChallengeInvitation =>
          inv !== null && inv.status === 'pending' && new Date(inv.expiresAt) > new Date()
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

    const populatedInvitations = invitations.map((inv) => populateInvitation(inv))

    return success(populatedInvitations)
  },

  // ============================================================================
  // Create Invitation
  // ============================================================================

  /**
   * Create a new invitation
   */
  async createInvitation(
    request: CreateInvitationRequest
  ): Promise<ApiResponse<ChallengeInvitation>> {
    await delay(150)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    // Validate challenge exists
    const challenge = mockDb.findById<Challenge>('challenges', request.challengeId)
    if (!challenge) {
      return error('Challenge not found')
    }

    // Validate recipients
    if (!request.recipients || request.recipients.length === 0) {
      return error('At least one recipient is required')
    }

    // Check max participants
    if (request.maxParticipants && request.recipients.length > request.maxParticipants) {
      return error('Too many recipients for max participants limit')
    }

    // Check recipient permissions
    for (const recipient of request.recipients) {
      if (recipient.type === 'user_id') {
        // Can't invite yourself
        if (recipient.value === userId) {
          return error('Cannot invite yourself')
        }

        const permission = mockDb.findOne<InvitationPermission>(
          'invitationPermissions',
          (p) => p.userId === recipient.value
        )

        if (permission) {
          if (permission.permissionType === 'deny_all') {
            return error('One or more users are not accepting invitations')
          }
          if (permission.permissionType === 'allow_contacts') {
            if (!permission.allowedUserIds?.includes(userId)) {
              return error('One or more users only accept invitations from contacts')
            }
          }
          if (permission.blockedUserIds?.includes(userId)) {
            return error('You cannot send invitations to one or more users')
          }
        }
      }
    }

    // Create expiration (1 year per spec)
    const expiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()

    // Create invitation
    const invitation = mockDb.insert<ChallengeInvitation>('challengeInvitations', {
      challengeId: request.challengeId,
      senderId: userId,
      paymentApproach: request.paymentApproach,
      proposedStartDate: request.proposedStartDate,
      message: request.message,
      maxParticipants: request.maxParticipants,
      status: 'pending',
      expiresAt,
    })

    // Create recipient records
    for (const recipient of request.recipients) {
      let recipientUserId: UUID | undefined

      // If type is user_id, set directly
      if (recipient.type === 'user_id') {
        recipientUserId = recipient.value
      } else if (recipient.type === 'email') {
        // Check if email matches an existing user
        const existingUser = mockDb.findOne<User>(
          'users',
          (u) => u.email.toLowerCase() === recipient.value.toLowerCase()
        )
        if (existingUser) {
          recipientUserId = existingUser.id
        }
      }

      mockDb.insert<InvitationRecipient>('invitationRecipients', {
        invitationId: invitation.id,
        recipientType: recipient.type,
        recipientValue: recipient.value,
        recipientUserId,
        status: 'pending',
      })
    }

    return success(populateInvitation(invitation))
  },

  // ============================================================================
  // Respond to Invitation
  // ============================================================================

  /**
   * Accept an invitation
   */
  async acceptInvitation(invitationId: UUID): Promise<ApiResponse<ChallengeInvitation>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const invitation = mockDb.findById<ChallengeInvitation>('challengeInvitations', invitationId)
    if (!invitation) {
      return error('Invitation not found')
    }

    if (invitation.status !== 'pending') {
      return error('Invitation is no longer pending')
    }

    // Check if expired
    if (new Date(invitation.expiresAt) < new Date()) {
      mockDb.update<ChallengeInvitation>('challengeInvitations', invitationId, { status: 'expired' })
      return error('Invitation has expired')
    }

    const user = mockDb.findById<User>('users', userId)
    if (!user) {
      return error('User not found')
    }

    // Find the recipient record for this user
    const recipientRecord = mockDb.findOne<InvitationRecipient>(
      'invitationRecipients',
      (r) =>
        r.invitationId === invitationId &&
        (r.recipientUserId === userId || r.recipientValue === user.email)
    )

    if (!recipientRecord) {
      return error('You are not a recipient of this invitation')
    }

    if (recipientRecord.status !== 'pending') {
      return error('You have already responded to this invitation')
    }

    // Update recipient record
    mockDb.update<InvitationRecipient>('invitationRecipients', recipientRecord.id, {
      status: 'accepted',
      respondedAt: timestamp(),
      recipientUserId: userId, // Link user if not already linked
    })

    // Check if all recipients have accepted
    const allRecipients = mockDb.findWhere<InvitationRecipient>(
      'invitationRecipients',
      (r) => r.invitationId === invitationId
    )
    const allAccepted = allRecipients.every((r) => r.status === 'accepted')

    if (allAccepted) {
      mockDb.update<ChallengeInvitation>('challengeInvitations', invitationId, {
        status: 'accepted',
      })
    }

    const updated = mockDb.findById<ChallengeInvitation>('challengeInvitations', invitationId)
    return success(populateInvitation(updated!))
  },

  /**
   * Decline an invitation
   */
  async declineInvitation(invitationId: UUID): Promise<ApiResponse<ChallengeInvitation>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const invitation = mockDb.findById<ChallengeInvitation>('challengeInvitations', invitationId)
    if (!invitation) {
      return error('Invitation not found')
    }

    if (invitation.status !== 'pending') {
      return error('Invitation is no longer pending')
    }

    const user = mockDb.findById<User>('users', userId)
    if (!user) {
      return error('User not found')
    }

    // Find the recipient record for this user
    const recipientRecord = mockDb.findOne<InvitationRecipient>(
      'invitationRecipients',
      (r) =>
        r.invitationId === invitationId &&
        (r.recipientUserId === userId || r.recipientValue === user.email)
    )

    if (!recipientRecord) {
      return error('You are not a recipient of this invitation')
    }

    if (recipientRecord.status !== 'pending') {
      return error('You have already responded to this invitation')
    }

    // Update recipient record
    mockDb.update<InvitationRecipient>('invitationRecipients', recipientRecord.id, {
      status: 'declined',
      respondedAt: timestamp(),
    })

    // If any recipient declines, mark invitation as declined
    mockDb.update<ChallengeInvitation>('challengeInvitations', invitationId, {
      status: 'declined',
    })

    const updated = mockDb.findById<ChallengeInvitation>('challengeInvitations', invitationId)
    return success(populateInvitation(updated!))
  },

  // ============================================================================
  // Cancel Invitation
  // ============================================================================

  /**
   * Cancel a sent invitation
   */
  async cancelInvitation(invitationId: UUID): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const invitation = mockDb.findById<ChallengeInvitation>('challengeInvitations', invitationId)
    if (!invitation) {
      return error('Invitation not found')
    }

    if (invitation.senderId !== userId) {
      return error('Not authorized')
    }

    if (invitation.status !== 'pending') {
      return error('Invitation can only be cancelled while pending')
    }

    mockDb.update<ChallengeInvitation>('challengeInvitations', invitationId, {
      status: 'cancelled',
    })

    return success(undefined)
  },

  // ============================================================================
  // Permissions
  // ============================================================================

  /**
   * Get invitation permissions
   */
  async getInvitationPermissions(): Promise<ApiResponse<InvitationPermission | null>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const permission = mockDb.findOne<InvitationPermission>(
      'invitationPermissions',
      (p) => p.userId === userId
    )

    return success(permission)
  },

  /**
   * Update invitation permissions
   */
  async updateInvitationPermissions(data: {
    permissionType: InvitationPermissionType
    allowedUserIds?: UUID[]
    blockedUserIds?: UUID[]
  }): Promise<ApiResponse<InvitationPermission>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    let permission = mockDb.findOne<InvitationPermission>(
      'invitationPermissions',
      (p) => p.userId === userId
    )

    if (permission) {
      const updated = mockDb.update<InvitationPermission>('invitationPermissions', permission.id, data)
      if (!updated) {
        return error('Failed to update permissions')
      }
      return success(updated)
    } else {
      permission = mockDb.insert<InvitationPermission>('invitationPermissions', {
        userId,
        ...data,
      })
      return success(permission)
    }
  },

  // ============================================================================
  // Recipient Search
  // ============================================================================

  /**
   * Search for potential invitation recipients
   */
  async searchRecipients(query: string): Promise<ApiResponse<InvitationRecipientSearchResult[]>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    if (!query || query.length < 2) {
      return success([])
    }

    const lowerQuery = query.toLowerCase()

    // Search users by display name or email
    const users = mockDb
      .findWhere<User>(
        'users',
        (u) =>
          u.id !== userId &&
          (u.displayName.toLowerCase().includes(lowerQuery) ||
            u.email.toLowerCase().includes(lowerQuery))
      )
      .slice(0, 10)

    // Check invitation permissions for each user
    const results: InvitationRecipientSearchResult[] = users.map((user) => {
      const permission = mockDb.findOne<InvitationPermission>(
        'invitationPermissions',
        (p) => p.userId === user.id
      )

      let canReceiveInvitations = true
      if (permission) {
        if (permission.permissionType === 'deny_all') {
          canReceiveInvitations = false
        } else if (permission.permissionType === 'allow_contacts') {
          canReceiveInvitations = permission.allowedUserIds?.includes(userId) ?? false
        }
        if (permission.blockedUserIds?.includes(userId)) {
          canReceiveInvitations = false
        }
      }

      return {
        id: user.id,
        displayName: user.displayName,
        email: user.email,
        avatarUrl: undefined,
        canReceiveInvitations,
      }
    })

    return success(results)
  },
}

// ============================================================================
// Helper Functions
// ============================================================================

function populateInvitation(invitation: ChallengeInvitation): ChallengeInvitation {
  // Populate challenge
  const challenge = mockDb.findById<Challenge>('challenges', invitation.challengeId)

  // Populate sender
  const sender = mockDb.findById<User>('users', invitation.senderId)

  // Get recipients
  const recipientRecords = mockDb.findWhere<InvitationRecipient>(
    'invitationRecipients',
    (r) => r.invitationId === invitation.id
  )

  // Populate recipient users
  const recipients = recipientRecords.map((r) => {
    let user: InvitationRecipient['user']
    if (r.recipientUserId) {
      const userRecord = mockDb.findById<User>('users', r.recipientUserId)
      if (userRecord) {
        user = {
          id: userRecord.id,
          displayName: userRecord.displayName,
          avatarUrl: undefined,
        }
      }
    }
    return { ...r, user }
  })

  // Calculate stats
  const acceptedCount = recipients.filter((r) => r.status === 'accepted').length
  const pendingCount = recipients.filter((r) => r.status === 'pending').length
  const declinedCount = recipients.filter((r) => r.status === 'declined').length

  return {
    ...invitation,
    challenge: challenge ?? undefined,
    sender: sender
      ? {
          id: sender.id,
          displayName: sender.displayName,
          avatarUrl: undefined,
        }
      : undefined,
    recipients,
    acceptedCount,
    pendingCount,
    declinedCount,
  }
}
